import { OrderedMap } from 'immutable';
import React, { forwardRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import SnackbarContent from '@mui/material/SnackbarContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SnackbarContentProps } from '@mui/material/SnackbarContent';

import CloseIcon from '@mui/icons-material/ClearRounded';

import { getLogger } from 'app-components/utils/core';

import SimplifiIcon from 'assets/logos/simplifi-logo.svg';
import QButton from 'components/QButton';
import { addNotifications, removeNotifications } from 'data/notifications/notificationsActions';
import { mkNotification } from 'data/notifications/notificationsTypes';

import {
  allowInstallPromptToBeDisabled,
  getActivePwaNotification,
  setActivePwaNotification,
  trackBrowserInstall,
  trackInstall,
  trackInstallDismissed,
  trackInstallPrompted,
} from './PwaHelpers';
import { useStyles } from './styles';

const log = getLogger('InstallViaInstallPrompt');

interface InstallPromptProps extends Omit<SnackbarContentProps, 'message'> {
  onDismiss: (disable: boolean) => void;
  onInstall: () => void;
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const InstallPrompt = forwardRef<HTMLDivElement, InstallPromptProps>((props, ref) => {
  const { onDismiss, onInstall } = props;
  const { classes } = useStyles();

  const allowDisable = allowInstallPromptToBeDisabled();

  return (
    <SnackbarContent
      className={classes.root}
      classes={{
        message: classes.muiMessage,
      }}
      ref={ref}
      message={
        <Paper className={classes.toast} elevation={3} square={false}>
          <IconButton
            className={classes.closeButton}
            aria-label='Dismiss'
            id='snackbar-dismiss'
            type='button'
            onClick={() => onDismiss(false)}
            size='large'
          >
            <CloseIcon />
          </IconButton>
          <img className={classes.icon} src={SimplifiIcon} alt='' />
          <Typography className={classes.title} variant='bodyMedium'>
            Install Quicken Simplifi
          </Typography>
          <Typography className={classes.message} variant='paragraphRegular'>
            Add it to your device and access instantly from your Applications.
          </Typography>
          <Stack gap={1}>
            <QButton id='button-install' onClick={() => onInstall()} variant='contained'>
              Install
            </QButton>
            <QButton
              className={classes.actionSecondary}
              id='button-dismiss'
              onClick={() => onDismiss(allowDisable)}
              variant={'outlined'}
            >
              {allowDisable ? "Don't Show Again" : 'Not Now'}
            </QButton>
          </Stack>
        </Paper>
      }
    />
  );
});

InstallPrompt.displayName = 'InstallPrompt';

const InstallViaInstallPrompt: React.FC = () => {
  log.info('Waiting for a beforeinstallprompt event...');
  const dispatch = useDispatch();

  useEffect(() => {
    const handleDismiss = (id: string, disable: boolean) => {
      trackInstallDismissed(disable);
      dispatch(removeNotifications([id]));
      setActivePwaNotification(null);
    };

    const handleInstall = (id: string, event: BeforeInstallPromptEvent) => {
      trackInstall();
      dispatch(removeNotifications([id]));

      // show native prompt
      event.prompt();

      // decide what to do after the user chooses
      event.userChoice
        .then((choice) => {
          trackBrowserInstall(choice.outcome === 'accepted');
        })
        .catch((error) => assert(false, error));

      setActivePwaNotification(null);
    };

    const beforeInstallPromptHandler = (event: BeforeInstallPromptEvent, count = 1) => {
      log.info('Received beforeInstallPrompt');
      event.preventDefault();

      // Do not show a PWA notification if one is already being shown. But, try again in a minute for up to 5 minutes.
      // Otherwise; won't show till at least next hard refresh.
      //
      if (getActivePwaNotification()) {
        if (count < 5) {
          setInterval(() => {
            beforeInstallPromptHandler(event, count + 1);
          }, 60000);
        }
        return;
      }

      trackInstallPrompted();
      setActivePwaNotification('PWA_INSTALL_PROMPT');

      const id = uuidv4();
      dispatch(
        addNotifications(
          OrderedMap([
            [
              id,
              mkNotification({
                id,
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
                snackbarContent: InstallPrompt,
                snackbarContentProps: {
                  onClose: () => handleDismiss(id, false),
                  onDismiss: (disable: boolean) => handleDismiss(id, disable),
                  onInstall: () => handleInstall(id, event),
                },
                autoHideDuration: 30000,
              }),
            ],
          ]),
        ),
      );
    };

    const beforeInstallPromptListener: EventListener = (event: BeforeInstallPromptEvent) =>
      beforeInstallPromptHandler(event);

    window.addEventListener('beforeinstallprompt', beforeInstallPromptListener);
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallPromptListener);
    };
  }, [dispatch]);

  return null;
};

export default InstallViaInstallPrompt;
